import type {
  CandidateDecision,
  CreateGroupResponse,
  DecideResponse,
  FlaggedGroupsResponse,
  GroupCandidateDetail,
  GroupCandidatesResponse,
  HealthDeepResponse,
  HealthResponse,
  MeResponse,
  ProductDetail,
  ProductsResponse,
  ResolveFlagRequest,
  ResolveFlagResponse,
  SuggestResponse,
} from '@stalvi/contracts';
import { getIdToken } from './firebase.js';
import { getSearchBackend } from './searchBackend';

const BASE = import.meta.env.VITE_API_BASE_URL || '/api';

const HTTP_STATUS_UNAUTHORIZED = 401;
const HTTP_STATUS_NO_CONTENT = 204;

export type AuthErrorKind = 'expired' | 'revoked' | 'unauthenticated' | null;

type AuthHandler = (kind: AuthErrorKind) => void;
let authHandler: AuthHandler = () => {};

export function setAuthErrorHandler(h: AuthHandler): void {
  authHandler = h;
}

interface FetchOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  retry?: boolean;
}

interface ApiErrorEnvelope {
  error?: { code?: string; message?: string };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly apiMessage: string,
  ) {
    super(apiMessage);
  }
}

function buildHeaders(token: string | null, hasBody: boolean): Record<string, string> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (token) headers.authorization = `Bearer ${token}`;
  if (hasBody) headers['content-type'] = 'application/json';
  return headers;
}

function buildRequestInit(method: string, headers: Record<string, string>, opts: FetchOpts): RequestInit {
  const init: RequestInit = { method, headers };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }
  if (opts.signal) init.signal = opts.signal;
  return init;
}

async function readErrorEnvelope(res: Response): Promise<ApiErrorEnvelope> {
  try {
    return (await res.json()) as ApiErrorEnvelope;
  } catch {
    return {};
  }
}

/**
 * Resolve the auth-error kind for a 401 response. The API distinguishes a
 * server-side revocation (`session_revoked`) from a plain expiry; on the
 * client these map to different toast/redirect flows.
 */
async function resolveAuthErrorKind(res: Response): Promise<AuthErrorKind> {
  let code = 'unauthorized';
  try {
    const j = (await res.clone().json()) as ApiErrorEnvelope;
    code = j.error?.message === 'session_revoked' ? 'session_revoked' : 'unauthorized';
  } catch {
    // ignore
  }
  return code === 'session_revoked' ? 'revoked' : 'expired';
}

async function handleUnauthorized(
  res: Response,
  initialToken: string | null,
  doFetch: (tok: string | null) => Promise<Response>,
  retry: boolean,
): Promise<Response> {
  if (retry) return res;
  // Try silent refresh once.
  const refreshed = await getIdToken(true).catch(() => null);
  let next = res;
  if (refreshed && refreshed !== initialToken) {
    next = await doFetch(refreshed);
  }
  if (next.status === HTTP_STATUS_UNAUTHORIZED) {
    authHandler(await resolveAuthErrorKind(next));
    throw new ApiError(HTTP_STATUS_UNAUTHORIZED, 'unauthorized', 'Sesión expirada');
  }
  return next;
}

async function throwApiError(res: Response): Promise<never> {
  let msg = res.statusText;
  let code = 'internal';
  const envelope = await readErrorEnvelope(res);
  msg = envelope.error?.message ?? msg;
  code = envelope.error?.code ?? code;
  throw new ApiError(res.status, code, msg);
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (res.status === HTTP_STATUS_NO_CONTENT) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

async function request<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const url = `${BASE}${path}`;
  const method = opts.method ?? 'GET';

  const initialToken = await getIdToken(false);
  const doFetch = async (tok: string | null): Promise<Response> => {
    const headers = buildHeaders(tok, opts.body !== undefined);
    const init = buildRequestInit(method, headers, opts);
    return fetch(url, init);
  };

  let res = await doFetch(initialToken);

  if (res.status === HTTP_STATUS_UNAUTHORIZED) {
    res = await handleUnauthorized(res, initialToken, doFetch, opts.retry === true);
  }

  // A 403 (not-allowlisted / email_unverified — distinct from a 401 revoked
  // session) just propagates: useAuth maps a thrown 403 from /me to the
  // not_allowed screen, so no auth-handler toast is fired here.
  if (!res.ok) {
    await throwApiError(res);
  }

  return parseResponse<T>(res);
}

// ---- Typed endpoints ----

export const api = {
  health: () => request<HealthResponse>('/v1/health'),
  healthDeep: () => request<HealthDeepResponse>('/v1/health/deep'),
  me: () => request<MeResponse>('/v1/me'),
  logout: () => request<void>('/v1/logout', { method: 'POST' }),
  suggest: (q: string, signal?: AbortSignal) => {
    // `?backend=` is threaded only for the non-default Algolia bake-off (#199);
    // the default gcp path keeps the exact #198 URL.
    const backend = getSearchBackend();
    const tail = backend !== 'gcp' ? `&backend=${backend}` : '';
    return request<SuggestResponse>(`/v1/search/suggest?q=${encodeURIComponent(q)}${tail}`, { signal });
  },
  products: (params: { q?: string; brands?: string[]; page?: number }) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.brands?.length) qs.set('brands', params.brands.join(','));
    if (params.page) qs.set('page', String(params.page));
    const backend = getSearchBackend();
    if (backend !== 'gcp') qs.set('backend', backend);
    const tail = qs.toString();
    return request<ProductsResponse>(`/v1/search/products${tail ? `?${tail}` : ''}`);
  },
  product: (productId: string) => request<ProductDetail>(`/v1/products/${encodeURIComponent(productId)}`),
  scanUnknown: (ean13: string) => request<void>('/v1/scan/unknown', { method: 'POST', body: { ean13 } }),

  // ---- Product grouping (TUC-data-0005 / issue #196) ----
  groupCandidates: (limit?: number) =>
    request<GroupCandidatesResponse>(`/v1/groups/candidates${limit ? `?limit=${limit}` : ''}`),
  groupCandidateDetail: (candidateKey: string) =>
    request<GroupCandidateDetail>(`/v1/groups/candidates/${encodeURIComponent(candidateKey)}`),
  decideCandidates: (decisions: CandidateDecision[]) =>
    request<DecideResponse>('/v1/groups/decisions', { method: 'POST', body: { decisions } }),
  createGroup: (ean13s: string[], note?: string) =>
    request<CreateGroupResponse>('/v1/groups', {
      method: 'POST',
      body: { ean13s, ...(note ? { note } : {}) },
    }),
  flaggedGroups: () => request<FlaggedGroupsResponse>('/v1/groups/flagged'),
  resolveFlag: (groupId: string, action: ResolveFlagRequest['action'], note?: string) =>
    request<ResolveFlagResponse>(`/v1/groups/${encodeURIComponent(groupId)}/resolve`, {
      method: 'POST',
      body: { action, ...(note ? { note } : {}) },
    }),
};
